import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import type { LeaveTypeId } from '@lms/shared-types'
import { ALL_LEAVE_TYPES } from '../../lib/leave-type-palette'
import type { TeamColorStyle } from '../../lib/team-color-palette'
import { useViewport } from '../../lib/use-viewport'
import { Icon } from './Icon'

export interface CalendarFilters {
  teams: ReadonlySet<string>
  leaveTypes: ReadonlySet<LeaveTypeId>
  showBlackouts: boolean
  showHolidays: boolean
  showPending: boolean
}

interface CalendarToolbarProps {
  filters: CalendarFilters
  onFiltersChange: (next: CalendarFilters) => void
  teamOptions: ReadonlyArray<{ id: string; name: string; headcount: number }>
  monthLabel: string
  onPrevMonth: () => void
  onNextMonth: () => void
  onToday: () => void
  /**
   * Whether the viewer may see pending leaves on the calendar. When false
   * the "Pending" toggle is hidden — backend won't return any pending rows
   * for this role anyway, so the chip would be a dead control.
   * Defaults to true to keep existing call sites + tests working.
   */
  canSeePending?: boolean
  /** True when team `id` is currently shown (checked). */
  teamChecked: (id: string) => boolean
  /** Toggle a single team's shown state. */
  onToggleTeam: (id: string) => void
  /** Show every team. */
  onSelectAllTeams: () => void
  /** Hide every team. */
  onClearTeams: () => void
  /**
   * Resolve a team's calendar colour so each row can show a swatch matching
   * its leave bars. Optional — omit to render the list without swatches.
   */
  teamColor?: (id: string) => TeamColorStyle
}

const DEFAULT_FILTERS: CalendarFilters = {
  teams: new Set(),
  leaveTypes: new Set(),
  showBlackouts: true,
  showHolidays: true,
  showPending: false,
}

function activeFilterCount(filters: CalendarFilters): number {
  let n = 0
  if (filters.teams.size > 0) n += 1
  if (filters.leaveTypes.size < ALL_LEAVE_TYPES.length) n += 1
  if (filters.showBlackouts !== DEFAULT_FILTERS.showBlackouts) n += 1
  if (filters.showHolidays !== DEFAULT_FILTERS.showHolidays) n += 1
  if (filters.showPending !== DEFAULT_FILTERS.showPending) n += 1
  return n
}

export function CalendarToolbar({
  filters,
  onFiltersChange,
  teamOptions,
  monthLabel,
  onPrevMonth,
  onNextMonth,
  onToday,
  canSeePending = true,
  teamChecked,
  onToggleTeam,
  onSelectAllTeams,
  onClearTeams,
  teamColor,
}: Readonly<CalendarToolbarProps>): JSX.Element {
  const [openMenu, setOpenMenu] = useState<'teams' | null>(null)
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false)
  const teamsRef = useRef<HTMLDivElement | null>(null)
  const mobileSheetRef = useRef<HTMLDivElement | null>(null)
  const mobileTriggerRef = useRef<HTMLButtonElement | null>(null)
  const { isMobile } = useViewport()

  useEffect(() => {
    if (openMenu === null) return
    let armed = false
    const armHandle = window.setTimeout(() => {
      armed = true
    }, 20)
    function onMouseDown(e: MouseEvent): void {
      if (!armed) return
      const target = e.target as Node
      const teamsEl = teamsRef.current
      if (openMenu === 'teams' && teamsEl !== null && teamsEl.contains(target)) return
      setOpenMenu(null)
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpenMenu(null)
    }
    window.addEventListener('mousedown', onMouseDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.clearTimeout(armHandle)
      window.removeEventListener('mousedown', onMouseDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [openMenu])

  // Close mobile filter sheet on outside click / Escape.
  useEffect(() => {
    if (!mobileSheetOpen) return
    let armed = false
    const armHandle = window.setTimeout(() => {
      armed = true
    }, 20)
    function onMouseDown(e: MouseEvent): void {
      if (!armed) return
      const target = e.target as Node
      const sheet = mobileSheetRef.current
      const trigger = mobileTriggerRef.current
      if (sheet !== null && sheet.contains(target)) return
      if (trigger !== null && trigger.contains(target)) return
      setMobileSheetOpen(false)
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setMobileSheetOpen(false)
    }
    window.addEventListener('mousedown', onMouseDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.clearTimeout(armHandle)
      window.removeEventListener('mousedown', onMouseDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [mobileSheetOpen])

  // When viewport flips back to desktop, ensure the mobile sheet isn't
  // left orphaned in an open state.
  useEffect(() => {
    if (!isMobile) setMobileSheetOpen(false)
  }, [isMobile])

  // Two-tone month label: month name bold, year muted — "**July** 2026".
  const [monthWord, ...monthRest] = monthLabel.split(' ')
  const monthYear = monthRest.join(' ')

  const monthNav = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {/* < MONTH > — chevrons flank a fixed-width label so the arrows don't
          shift as the month name changes length. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button
          type="button"
          data-testid="toolbar-prev"
          onClick={onPrevMonth}
          aria-label="Previous month"
          className="tc-navbtn"
        >
          <Icon name="chevron_left" size={18} aria-hidden="true" />
        </button>
        <span className="tc-month" style={{ width: isMobile ? 130 : 150 }}>
          {monthWord}
          {monthYear ? <span className="tc-month-year"> {monthYear}</span> : null}
        </span>
        <button
          type="button"
          data-testid="toolbar-next"
          onClick={onNextMonth}
          aria-label="Next month"
          className="tc-navbtn"
        >
          <Icon name="chevron_right" size={18} aria-hidden="true" />
        </button>
      </div>
      <button
        type="button"
        data-testid="toolbar-today"
        onClick={onToday}
        className="tc-dd"
      >
        Today
      </button>
    </div>
  )

  const renderTeamsMenu = (wide: boolean): JSX.Element => {
    const wrapperStyle: CSSProperties = wide
      ? { position: 'relative', width: '100%' }
      : { position: 'relative' }
    const panelStyle: CSSProperties = wide
      ? { ...menuPanelStyle, left: 0, right: 0, minWidth: 0 }
      : menuPanelStyle
    return (
      <div ref={teamsRef} style={wrapperStyle}>
        <button
          type="button"
          data-testid="teams-menu-summary"
          onClick={() => setOpenMenu(openMenu === 'teams' ? null : 'teams')}
          aria-expanded={openMenu === 'teams'}
          aria-haspopup="listbox"
          className="tc-dd"
          style={wide ? { width: '100%', justifyContent: 'space-between' } : undefined}
        >
          <Icon
            name="people"
            size={16}
            aria-hidden="true"
            style={{ color: 'var(--steel-blue)' }}
          />
          <span>
            {/* Count the checked teams among the live options. */}
            {(() => {
              if (teamOptions.length === 0) return 'Teams'
              const n = teamOptions.filter((o) => teamChecked(o.id)).length
              return `Teams (${n})`
            })()}
          </span>
          <Icon
            name="expand_more"
            size={18}
            aria-hidden="true"
            style={{
              color: 'var(--text-3)',
              transition: 'transform .14s',
              transform:
                openMenu === 'teams' ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </button>
        {openMenu === 'teams' ? (
          <div role="listbox" style={panelStyle}>
            {teamOptions.length === 0 ? (
              <span style={emptyStyle}>No teams available</span>
            ) : (
              <>
                {/* Bulk actions — select all / clear the whole list. */}
                <div className="tc-team-actions">
                  <button
                    type="button"
                    data-testid="teams-select-all"
                    className="tc-linkbtn"
                    onClick={onSelectAllTeams}
                  >
                    Select all
                  </button>
                  <span aria-hidden className="tc-team-actions-sep">·</span>
                  <button
                    type="button"
                    data-testid="teams-clear"
                    className="tc-linkbtn"
                    onClick={onClearTeams}
                  >
                    Clear
                  </button>
                </div>
                {teamOptions.map((t) => (
                  <label
                    key={t.id}
                    data-testid={`team-option-${t.id}`}
                    style={menuItemStyle}
                  >
                    <input
                      type="checkbox"
                      checked={teamChecked(t.id)}
                      onChange={() => onToggleTeam(t.id)}
                      // Tint the checkbox with the team's calendar colour so the
                      // filter reads as the same colour language as the grid.
                      // Uses the darker `line` shade so the white tick stays
                      // legible against the otherwise-pastel fill.
                      style={
                        teamColor ? { accentColor: teamColor(t.id).line } : undefined
                      }
                    />
                    <span>{t.name}</span>
                    <span className="tc-dd-sub">{t.headcount}</span>
                  </label>
                ))}
              </>
            )}
          </div>
        ) : null}
      </div>
    )
  }

  // Toggle buttons — prototype `.tc-toggle` / `.tc-toggle.off` pill style.
  // Wrapped in spans for stable test selectors.
  //
  // Blackouts + public holidays are ALWAYS shown — hiding them makes no sense
  // (you don't plan around a freeze window or a holiday by filtering it off the
  // calendar), so those toggles were removed. The only remaining show/hide
  // control is Pending (other people's not-yet-approved leaves).
  const toggleChips = (
    <>
      {canSeePending ? (
        <span data-testid="toggle-pending">
          <button
            type="button"
            className={`tc-toggle${filters.showPending ? '' : ' off'}`}
            aria-pressed={filters.showPending}
            onClick={() =>
              onFiltersChange({ ...filters, showPending: !filters.showPending })
            }
          >
            <Icon name="schedule" size={15} aria-hidden="true" />
            Pending
          </button>
        </span>
      ) : null}
    </>
  )

  if (isMobile) {
    const activeCount = activeFilterCount(filters)
    return (
      <div
        data-testid="calendar-toolbar"
        className="tc-toolbar"
        style={{ flexDirection: 'column', position: 'relative' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          {monthNav}
          <button
            ref={mobileTriggerRef}
            type="button"
            data-testid="mobile-filters-trigger"
            onClick={() => setMobileSheetOpen((v) => !v)}
            aria-expanded={mobileSheetOpen}
            aria-controls="calendar-mobile-filters"
            className="tc-dd"
            style={
              activeCount > 0
                ? { color: 'var(--steel-blue-3)', borderColor: 'var(--steel-blue)' }
                : undefined
            }
          >
            Filters{activeCount > 0 ? ` (${activeCount})` : ''} ▾
          </button>
        </div>

        {/* On mobile, the panel renders inline below when open. When closed,
            we still mount the controls (display:none) so test IDs probed by
            JSDOM-default layouts remain queryable. */}
        {mobileSheetOpen ? (
          <div
            ref={mobileSheetRef}
            id="calendar-mobile-filters"
            data-testid="mobile-filters-sheet"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              padding: 14,
              background: 'var(--surface)',
              border: '1px solid var(--line-strong)',
              borderRadius: 10,
              boxShadow: 'var(--shadow-2)',
            }}
          >
            {teamOptions.length > 0 ? (
              <FilterSection label="Teams">
                {renderTeamsMenu(true)}
              </FilterSection>
            ) : null}
            <FilterSection label="Show">
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                }}
              >
                {toggleChips}
              </div>
            </FilterSection>
          </div>
        ) : (
          <div aria-hidden style={{ display: 'none' }}>
            {renderTeamsMenu(false)}
            {toggleChips}
          </div>
        )}
      </div>
    )
  }

  return (
    <div data-testid="calendar-toolbar" className="tc-toolbar">
      {monthNav}

      <span aria-hidden className="tc-spacer" />

      {teamOptions.length > 0 ? renderTeamsMenu(false) : null}
      {toggleChips}
    </div>
  )
}

interface FilterSectionProps {
  label: string
  children: React.ReactNode
}

function FilterSection({
  label,
  children,
}: Readonly<FilterSectionProps>): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontFamily: 'var(--font-mono, inherit)',
        }}
      >
        {label}
      </span>
      {children}
    </div>
  )
}

const menuPanelStyle: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: 4,
  minWidth: 220,
  background: 'var(--surface)',
  border: '1px solid var(--line-strong)',
  borderRadius: 8,
  boxShadow: 'var(--shadow-2)',
  padding: 6,
  zIndex: 50,
}

const menuItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 8px',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 13,
}

const emptyStyle: CSSProperties = {
  display: 'block',
  padding: '6px 8px',
  fontSize: 12,
  color: 'var(--text-3)',
  fontStyle: 'italic',
}
